import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom'
import Hero from './Hero'
import Features from './Features'
import SocialProof from './SocialProof'
import CTA from './CTA'
import Footer from './Footer'
import SignIn from './SignIn'
import SignUp from './SignUp'
import CreateClub from './CreateClub'
import Dashboard from './Dashboard'
import ClubView from './ClubView'
import ClubStats from './ClubStats'
import MyStats from './MyStats'
import JoinClub from './JoinClub'
import MyPastReadsList from './MyPastReadsList'
import MyToReadList from './MyToReadList'
import PastReadsList from './PastReadsList'
import ForgotPassword from './ForgotPassword'
import ResetPassword from './ResetPassword'
import About from './About'
import Disclosure from './Disclosure'
import CsvImport from './CsvImport'
import Admin from './Admin'
import ImpersonationBanner from './ImpersonationBanner'
import PublicVote from './PublicVote'
import PinLogin from './PinLogin'

type User = {
  id: number
  name: string
  email: string
  user_type?: 'member' | 'admin'
  preferred_affiliate_provider?: string
} | null

type SessionData = {
  user: {
    id: number
    name: string
    email: string
    user_type: 'member' | 'admin'
    preferred_affiliate_provider?: string
  }
  impersonating?: boolean
  true_user?: {
    id: number
    name: string
    email: string
  }
}

type ModalType = 'signin' | 'signup' | 'createclub' | 'forgotpassword' | null

function LandingPage({ onStartClub, user, onSignOut, onSignIn }: { onStartClub: () => void; user: User; onSignOut: () => void; onSignIn: () => void }) {
  return (
    <>
      <Hero onStartClub={onStartClub} user={user} onSignOut={onSignOut} onSignIn={onSignIn} />
      <Features />
      <Footer />
    </>
  )
}

function DashboardPage({ user, onSignOut, onCreateClub, refreshKey }: { user: User; onSignOut: () => void; onCreateClub: () => void; refreshKey: number }) {
  const navigate = useNavigate()

  if (!user) {
    return <Navigate to="/" replace />
  }

  return (
    <Dashboard
      key={refreshKey}
      user={user}
      onSignOut={onSignOut}
      onClubClick={(clubId) => navigate(`/clubs/${clubId}`)}
      onCreateClub={onCreateClub}
    />
  )
}

function ClubPage({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  if (!user) {
    return <Navigate to="/" replace />
  }

  if (!id) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <ClubView
      clubId={parseInt(id, 10)}
      user={user}
      onBack={() => navigate('/dashboard')}
      onSignOut={onSignOut}
    />
  )
}

function MyPastReadsPage({ user }: { user: User }) {
  const navigate = useNavigate()

  if (!user) {
    return <Navigate to="/" replace />
  }

  return (
    <MyPastReadsList
      onBack={() => navigate('/dashboard')}
    />
  )
}

function MyToReadPage({ user }: { user: User }) {
  const navigate = useNavigate()

  if (!user) {
    return <Navigate to="/" replace />
  }

  return (
    <MyToReadList
      onBack={() => navigate('/dashboard')}
    />
  )
}

function ClubPastReadsPage({ user }: { user: User }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [clubName, setClubName] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchClubName = async () => {
      if (!id) return
      try {
        const response = await fetch(`/api/v1/clubs/${id}`)
        if (response.ok) {
          const data = await response.json()
          setClubName(data.club.name)
        }
      } catch (err) {
        console.error('Failed to fetch club:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchClubName()
  }, [id])

  if (!user) {
    return <Navigate to="/" replace />
  }

  if (!id) {
    return <Navigate to="/dashboard" replace />
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <PastReadsList
      clubId={parseInt(id, 10)}
      clubName={clubName}
      onBack={() => navigate(`/clubs/${id}`)}
    />
  )
}

function CsvImportPage({ user }: { user: User }) {
  if (!user) {
    return <Navigate to="/" replace />
  }

  return <CsvImport />
}

function AdminPage({ user, onImpersonationStart }: { user: User; onImpersonationStart: () => void }) {
  const navigate = useNavigate()

  if (!user) {
    return <Navigate to="/" replace />
  }

  return (
    <Admin
      user={user}
      onBack={() => navigate('/dashboard')}
      onImpersonationStart={onImpersonationStart}
    />
  )
}

const isPwa = typeof window !== 'undefined' && (
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone === true
)

function AppRoutes() {
  const [user, setUser] = useState<User>(null)
  const [modal, setModal] = useState<ModalType>(null)
  const [loading, setLoading] = useState(true)
  const [dashboardKey, setDashboardKey] = useState(0)
  const [impersonating, setImpersonating] = useState(false)
  const [trueUser, setTrueUser] = useState<{ id: number; name: string; email: string } | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    checkCurrentUser()
  }, [])

  const checkCurrentUser = async () => {
    try {
      const response = await fetch('/api/v1/me')
      if (response.ok && response.status !== 204) {
        const data: SessionData = await response.json()
        setUser(data.user)
        setImpersonating(data.impersonating || false)
        setTrueUser(data.true_user || null)
      }
    } catch (err) {
      console.error('Error checking current user:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStartClub = () => {
    if (user) {
      setModal('createclub')
    } else {
      // Invite-only: redirect to sign in instead of sign up
      setModal('signin')
    }
  }

  const handleSignIn = () => {
    setModal('signin')
  }

  const handleSignOut = async () => {
    try {
      const { fetchWithCsrf } = await import('../utils/csrf')
      await fetchWithCsrf('/api/v1/logout', { method: 'DELETE' })
      setUser(null)
      navigate('/')
    } catch (err) {
      console.error('Error signing out:', err)
    }
  }

  const handleSignInSuccess = (user: User) => {
    setUser(user)
    setModal(null)
    // If user is on a join page, stay there so the join flow can complete
    if (!location.pathname.startsWith('/join/')) {
      navigate('/dashboard')
    }
  }

  const handleClubCreated = (club: { id: number; name: string; created_at: string }) => {
    console.log('Club created:', club)
    setModal(null)
    setDashboardKey(prev => prev + 1) // Force Dashboard to remount and refresh data
    navigate('/dashboard')
  }

  const handleImpersonationStart = () => {
    // Refresh user data to get impersonation state
    checkCurrentUser().then(() => {
      navigate('/dashboard')
    })
  }

  const handleStopImpersonating = () => {
    // Refresh user data to clear impersonation state
    checkCurrentUser().then(() => {
      navigate('/admin')
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {impersonating && user && trueUser && (
        <ImpersonationBanner
          impersonatedUser={user}
          trueUser={trueUser}
          onStopImpersonating={handleStopImpersonating}
        />
      )}
      <Routes>
        <Route
          path="/"
          element={
            isPwa && !user
              ? <Navigate to="/pin-login" replace />
              : <LandingPage onStartClub={handleStartClub} user={user} onSignOut={handleSignOut} onSignIn={handleSignIn} />
          }
        />
        <Route
          path="/pin-login"
          element={
            user
              ? <Navigate to="/dashboard" replace />
              : <PinLogin onSuccess={handleSignInSuccess} onPasswordLogin={() => setModal('signin')} />
          }
        />
        <Route
          path="/dashboard"
          element={<DashboardPage user={user} onSignOut={handleSignOut} onCreateClub={handleStartClub} refreshKey={dashboardKey} />}
        />
        <Route
          path="/my-past-reads"
          element={<MyPastReadsPage user={user} />}
        />
        <Route
          path="/my-to-read"
          element={<MyToReadPage user={user} />}
        />
        <Route
          path="/my-stats"
          element={<MyStats />}
        />
        <Route
          path="/import"
          element={<CsvImportPage user={user} />}
        />
        <Route
          path="/admin"
          element={<AdminPage user={user} onImpersonationStart={handleImpersonationStart} />}
        />
        <Route
          path="/clubs/:id"
          element={<ClubPage user={user} onSignOut={handleSignOut} />}
        />
        <Route
          path="/clubs/:id/stats"
          element={<ClubStats />}
        />
        <Route
          path="/clubs/:id/past-reads"
          element={<ClubPastReadsPage user={user} />}
        />
        <Route
          path="/vote/:token"
          element={<PublicVote />}
        />
        <Route
          path="/join/:token"
          element={<JoinClub user={user} onSignInClick={() => setModal('signin')} onSignUpClick={() => setModal('signup')} />}
        />
        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />
        <Route
          path="/about"
          element={<About />}
        />
        <Route
          path="/disclosure"
          element={<Disclosure />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {modal === 'signin' && (
        <SignIn
          onClose={() => setModal(null)}
          onSignUpClick={() => setModal('signup')}
          onForgotPasswordClick={() => setModal('forgotpassword')}
          onSuccess={handleSignInSuccess}
        />
      )}

      {modal === 'forgotpassword' && (
        <ForgotPassword
          onClose={() => setModal(null)}
          onBackToSignIn={() => setModal('signin')}
        />
      )}

      {modal === 'signup' && (
        <SignUp
          onClose={() => setModal(null)}
          onSignInClick={() => setModal('signin')}
          onSuccess={handleSignInSuccess}
        />
      )}

      {modal === 'createclub' && (
        <CreateClub
          onClose={() => setModal(null)}
          onSuccess={handleClubCreated}
        />
      )}
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  )
}
